import React, { useMemo } from 'react'
import AsyncDropdown, { PropsWithAsyncDropdown } from 'core/elements/dropdown/AsyncDropdown'
import useListAction from 'core/hooks/useListAction'
import { listClusterHostGroups } from './actions'
import { useSelector } from 'react-redux'
import { clusterHostGroupsSelector } from './selectors'
import Tooltip, { TooltipProps } from 'core/elements/tooltip/Tooltip'
import { DropdownItemSpec } from 'core/elements/dropdown/DropdownCommonProps'

interface Props {
  label?: string
  width?: number
  fullWidth?: boolean
  tooltip?: string
  tooltipProps?: TooltipProps
  includeNone?: boolean
  disableClusterWithoutHosts?: boolean
  filterFn?: (item: any) => boolean
  optionMapFn?: (value: any) => DropdownItemSpec<string>
}

export default function ClusterHostGroupsPicklist({
  width = 180,
  fullWidth = false,
  label,
  tooltip = '',
  tooltipProps,
  includeNone = false,
  disableClusterWithoutHosts = false, // Mutually exclusive
  filterFn,
  optionMapFn, // Mutually exclusive
  ...rest
}: PropsWithAsyncDropdown<Props>) {
  const { loading } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const items = useMemo(() => {
    const filteredHostGroups = filterFn ? clusterHostGroups.filter(filterFn) : clusterHostGroups
    const mapFn = optionMapFn
      ? optionMapFn
      : (clusterHostGroup) => {
          const disabled = disableClusterWithoutHosts && clusterHostGroup.hosts?.length === 0
          return {
            label: `${clusterHostGroup.name} ${disabled ? ' (No Hosts Assigned)' : ''}`,
            value: clusterHostGroup.name,
            disabled,
          }
        }
    const options = filteredHostGroups.map(mapFn).sort((a, b) => {
      // Sort disabled items to the end
      if (a.disabled && !b.disabled) return 1
      if (!a.disabled && b.disabled) return -1
      return 0
    })
    if (!includeNone) {
      return options
    }

    return [
      {
        label: '-- None --',
        value: '',
      },
      ...options,
    ]
  }, [clusterHostGroups, includeNone, disableClusterWithoutHosts, filterFn])

  return (
    <Tooltip message={tooltip} {...tooltipProps}>
      <AsyncDropdown
        name={label}
        label={label}
        items={items}
        formField
        loading={loading}
        width={fullWidth ? undefined : width}
        {...rest}
      />
    </Tooltip>
  )
}
